import React from "react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PrintIcon from "@mui/icons-material/Print";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { neutral } from "app/themes/colors";
import Dialog from "components/dialog";
import { SignDocumentIcon } from "components/icons";
import SaveToEdocsDialog from "components/save-to-edocs-dialog";
import SignaturePicker from "components/signature-picker";

import PdfDocumentContainer from "./pdf-document-container.component";
import usePreviewPdfForm from "./use-preview-pdf-form.hook";

const actionButtonStyles = {
  flex: 1,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 0
};

const PreviewPdfFormDialog = () => {
  const {
    folderPickerOpen,
    isEdocsAvailable,
    boxFolderId,
    boxAccessToken,
    handleShowSignModal,
    handleHideSignModal,
    handleClosePreviewModal,
    handlePrint,
    handleSaveToEdocs,
    handleSubmitSignature,
    handleHideBoxDialog,
    handleSelectBoxFolder,
    fileName,
    open,
    isReady,
    showSignModal,
    title,
    numPages,
    pdfBlobUrl,
    pdfContainerRef,
    showSignButton
  } = usePreviewPdfForm();

  return (
    <Dialog
      title={title || "Preview PDF"}
      minWidth={1224}
      onClose={handleClosePreviewModal}
      open={open}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "flex-end",
          p: 3,
          pt: 0,
          flexDirection: "column"
        }
      }}
      paperContentSx={{
        height: "90%",
        minHeight: "640px"
      }}
    >
      <Stack
        direction="row"
        mb={2}
        columnGap={3}
        mt={-1.5}
        width="100%"
        justifyContent="space-between"
      >
        <Stack width="100%" direction="row" columnGap={3}>
          {showSignButton && (
            <Box>
              <IconButton
                data-testid="sign-button"
                size="small"
                sx={actionButtonStyles}
                disabled={!isReady}
                onClick={handleShowSignModal}
              >
                <SignDocumentIcon
                  style={{
                    height: "24px",
                    width: "24px",
                    fill: !isReady ? neutral[400] : neutral[800]
                  }}
                />
                <Typography>Sign</Typography>
              </IconButton>
            </Box>
          )}

          <Box>
            <IconButton
              data-testid="print-button"
              size="small"
              sx={actionButtonStyles}
              onClick={handlePrint}
              disabled={!isReady}
            >
              <PrintIcon />
              <Typography>Print</Typography>
            </IconButton>
          </Box>
          <Box>
            <IconButton
              data-testid="upload-button"
              size="small"
              sx={actionButtonStyles}
              onClick={handleSaveToEdocs}
              disabled={!isReady}
            >
              <CloudUploadIcon />
              <Typography>Upload to&nbsp;E-Docs</Typography>
            </IconButton>
          </Box>
        </Stack>
        <Stack width="100%" direction="row" columnGap={3}></Stack>
      </Stack>
      <PdfDocumentContainer
        pdfContainerRef={pdfContainerRef}
        numPages={numPages}
        pdfBlobUrl={pdfBlobUrl}
        isReady={isReady}
      />
      <Button
        size="small"
        variant="contained"
        sx={{ mt: 2 }}
        onClick={handleClosePreviewModal}
      >
        Close
      </Button>
      <Box>
        <SignaturePicker
          open={showSignModal}
          onClose={handleHideSignModal}
          onSubmit={handleSubmitSignature}
          enableHandSignatureOnly
          enableTypedSignature
          showUploadToEdocsCheckbox
          signatureDeskTitle="Patient Signature"
          submitButtonText="Sign Form"
          entryPoint="pdf-form"
          largeSize
        />
      </Box>
      <SaveToEdocsDialog
        open={folderPickerOpen}
        folderId={isEdocsAvailable ? boxFolderId : null}
        token={isEdocsAvailable ? boxAccessToken : null}
        suggestedFileName={fileName}
        onChoose={handleSelectBoxFolder}
        onClose={handleHideBoxDialog}
      />
    </Dialog>
  );
};

export default React.memo(PreviewPdfFormDialog);
